import type { SanitizedHtml } from '@hc-frontend/core-types';
import { filterXSS } from 'xss';

/**
 * Sanitize untrusted HTML (to prevent XSS).
 *
 * @param str - string to sanitize
 *
 * @returns sanitized string
 *
 * @example
 * ```ts
 * const result = xss('<script>alert("xss")</script>');
 * console.log(result);
 * // => '&lt;script&gt;alert("xss")&lt;/script&gt;
 * ```
 */
function xss(str: string): string {
  return filterXSS(str);
}

/**
 * Sanitize an HTML string and return it as {@link SanitizedHtml}, safe to
 * pass to `dangerouslySetInnerHTML`. Strips tags and attributes not on the
 * xss library allowlist.
 *
 * @param str - raw HTML string to sanitize
 * @returns sanitized HTML marked as safe for rendering
 */
function sanitizeHtml(str: string): SanitizedHtml {
  return filterXSS(str) as SanitizedHtml;
}

/**
 * HTML-encode a plain-text value so it can be safely interpolated into an
 * HTML string without introducing markup. Escapes `& < > " '`.
 *
 * @param str - plain text to encode
 * @returns HTML-entity-escaped string
 */
function encodeForHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export { encodeForHtml, sanitizeHtml, xss };
